import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Bell } from "lucide-react";

import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

type Prefs = { notify_enabled: boolean; notify_lang: "az" | "en" | "ru" };

export function GuardianNotifyCard({ personId }: { personId: string }) {
    const { t } = useTranslation();
    const { hasPerm } = useAuth();
    const canEdit = hasPerm("people.edit");
    const [p, setP] = useState<Prefs | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get(`/notification-center/guardians/${personId}`).then(r => setP(r.data)).catch(() => setP(null));
    }, [personId]);
    if (!p) return null;

    const save = async (patch: Partial<Prefs>) => {
        setBusy(true);
        try {
            const r = await api.patch(`/notification-center/guardians/${personId}`, patch);
            setP(r.data); toast.success(t("notif.guardian.saved"));
        } catch (e: any) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };

    return (
        <div className="surface-card p-5 flex flex-wrap items-center gap-4" data-testid="guardian-notify-card">
            <Bell size={18} style={{ color: "var(--text-muted)" }} />
            <div className="flex-1 min-w-[180px]">
                <div className="text-sm font-medium" style={{ color: "var(--text)" }}>{t("notif.guardian.title")}</div>
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("notif.guardian.hint")}</div>
            </div>
            <label className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--text)" }}>
                <input type="checkbox" data-testid="guardian-notify-switch" checked={p.notify_enabled} disabled={!canEdit || busy}
                    onChange={e => save({ notify_enabled: e.target.checked })} className="h-4 w-4" />
                {t("notif.guardian.send")}
            </label>
            <label className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
                {t("notif.guardian.language")}
                <select data-testid="guardian-notify-lang" value={p.notify_lang} disabled={!canEdit || busy}
                    onChange={e => save({ notify_lang: e.target.value as Prefs["notify_lang"] })}
                    className="surface-input px-2 py-1 text-sm">
                    <option value="az">Azərbaycan</option><option value="en">English</option><option value="ru">Русский</option>
                </select>
            </label>
        </div>
    );
}
